import { useState, useEffect } from "react";
import Joi, { version } from "joi";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

const useCreateTenantModal = ({
  isOpen,
  onRequestClose,
  setCurrentPage,
  setSearchTerm,
  fetchTenants,
  tenantsPerPage,
  excludeInactive,
}) => {
  const { authData } = useSelector((state) => state.loginSlice);
  const [currentStep, setCurrentStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [customLoader, setCustomLoader] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [skipLoader, setSkipLoader] = useState(false);
  const [isStandard, setIsStandard] = useState("standard");
  const [totalSteps, setTotalSteps] = useState(3);
  const [formData, setFormData] = useState({
    userName: "",
    email: "",
    phone: "",
    storeName: "",
    store_url: "",
    storeType: "",
    storeTypeId: "",
    subscriptionType: "Monthly",
    selectedPlan: "",
    api_version: "",
    access_token: "",
    brandLogo: null,
    iCurrencyId: "",
    storeConfigKeys: [],
    selectedPlan: "",
  });
  const [selectOptions, setSelectOptions] = useState({
    storeTypes: [],
    currency: [],
  });
  const dispatch = useDispatch();

  const [errors, setErrors] = useState({});

  const fetchPlans = async () => {
    try {
      setCustomLoader(true);
      let url = `${endpoint.GET_PLAN_BILLINGCYCLES}?bIsStandard=${
        isStandard === "standard" ? 1 : 0
      }`;
      const response = await apiHandler({
        url: url,
        method: "GET",
        authToken: authData,
        dispatch,
      });
      if (response.data.success) {
        setPlans(response.data.data);
        setFormData({
          ...formData,
          ["subscriptionType"]:
            response.data.data.length > 0
              ? response.data.data[0]?.iBillingCycleId
              : "",
        });
      }
    } catch (error) {
     
    } finally {
      setCustomLoader(false);
    }
  };

  const fetchFormData = async () => {
    try {
      let url = `${endpoint.GET_FORMDATA}`;
      const response = await apiHandler({
        url: url,
        method: "GET",
        authToken: authData,
        dispatch,
      });
      if (response.data.success) {
        setSelectOptions({
          storeTypes: response.data.data.storeTypes,
          currency: response.data.data.currencies,
        });
      }
    } catch (error) {
    
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchFormData();
  }, [isStandard]);

  const step1Schema = Joi.object({
    userName: Joi.string()
      .min(3)
      .required()
      .messages({
        "string.base": "Company Name must be a valid text.",
        "string.empty": "Company Name is required.",
        "string.min": "Company Name must be at least {#limit} characters long.",
        "any.required": "Company Name is required.",
      })
      .label("Company Name"),

    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        "string.base": "Email must be a valid text.",
        "string.empty": "Email is required.",
        "string.email": "Please enter a valid email address.",
        "any.required": "Email is required.",
      })
      .label("Email"),
  });

  const step2Schema = Joi.object({
    storeType: Joi.string().required().label("Store Type").messages({
      "any.required": "Store Type is required",
      "string.empty": "Store Type cannot be empty",
    }),

    storeName: Joi.string().min(3).required().label("Store Name").messages({
      "any.required": "Store Name is required",
      "string.empty": "Store Name cannot be empty",
      "string.min": "Store Name must be at least 3 characters long",
    }),

    brandLogo: Joi.any().required().label("Brand Logo").messages({
      "any.required": "Brand Logo is required",
      "string.empty": "Brand Logo cannot be empty",
      "string.uri": "Brand Logo must be a valid URL",
    }),

    currency: Joi.number().required().label("Currency").messages({
      "any.required": "Currency is required",
      "number.base": "Currency is required",
    }),

    // Conditional validation: Required only when storeType is "shopify"
    api_version: Joi.when("storeType", {
      is: "Shopify",
      then: Joi.string().required().label("API Version").messages({
        "any.required": "API Version is required when Store Type is Shopify",
        "string.empty": "API Version cannot be empty",
      }),
      otherwise: Joi.string().allow("").label("API Version"),
    }),
    access_token: Joi.when("storeType", {
      is: "Shopify",
      then: Joi.string().required().label("Access Token").messages({
        "any.required": "Access Token is required when Store Type is Shopify",
        "string.empty": "Access Token cannot be empty",
      }),
      otherwise: Joi.string().allow("").label("Access Token"),
    }),
    store_url: Joi.when("storeType", {
      is: "Shopify",
      then: Joi.string().required().label("Store URL").messages({
        "any.required": "Store URL is required when Store Type is Shopify",
        "string.empty": "Store URL cannot be empty",
        "string.uri": "Store URL must be a valid URL",
      }),
      otherwise: Joi.string().allow(""),
    }),
  });
  const step3Schema = Joi.object({
    subscriptionType: Joi.number().required(),
    selectedPlan: Joi.string().required(),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = async (e) => {
    const { files, name } = e.target;
    setFormData({
      ...formData,
      [name]: files[0],
    });
  };

  const handlePlanSelection = (planId) => {
    setFormData({
      ...formData,
      selectedPlan: String(planId),
    });
  };

  const handleSubscriptionTypeSelection = (type) => {
    setFormData({
      ...formData,
      subscriptionType: type,
    });
  };

  const getPlanPrice = (plan) => {
    const selectedPrice = plan.prices?.find(
      (price) => price.sBillingCycle === formData.subscriptionType
    );
    if (!selectedPrice) return "N/A";
    return `₹${selectedPrice.dPrice.toFixed(2)} `;
  };

  const getFieldDescription = (key) => {
    if (key.includes("url")) return "Enter the URL of your store";
    if (key.includes("token")) return "Provide the authentication token";
    if (key.includes("key")) return "Enter the API key for access";
    if (key.includes("version")) return "Specify the API version in use";

    return "Provide the required information";
  };

  const validateCurrentStep = () => {
    let schema;

    if (currentStep === 1) {
      schema = step1Schema;
    } else if (currentStep === 2) {
      schema = step2Schema;
    } else if (currentStep === 3) {
      schema = step3Schema;
    }

    const stepData =
      currentStep === 1
        ? {
            userName: formData.userName,
            email: formData.email,
          }
        : currentStep === 2
        ? {
            storeType: formData.storeType,
            storeName: formData.storeName,
            api_version: formData.api_version,
            access_token: formData.access_token,
            store_url: formData.store_url,
            brandLogo: formData.brandLogo,
            currency: parseInt(formData.iCurrencyId),
          }
        : {
            subscriptionType: formData.subscriptionType,
            selectedPlan: formData.selectedPlan,
          };

    const { error } = schema.validate(stepData, { abortEarly: false });
    if (!error) return null;

    const validationErrors = {};
    error.details.forEach((item) => {
      validationErrors[item.path[0]] = item.message;
    });
    return validationErrors;
  };

  const nextStep = () => {
    const validationErrors = validateCurrentStep();

    if (validationErrors) {
      setErrors(validationErrors);
    } else {
      setErrors({});
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const prevStep = () => setCurrentStep((prevStep) => prevStep - 1);

  const handleFormSubmit = async () => {
    // toast.success("Tenant Created");
    // onRequestClose();
    const validationErrors = validateCurrentStep();

    if (validationErrors) {
      setErrors(validationErrors);
    } else {
      createTenant(setSubmitLoader);
    }
  };

  const handleFormSkip = async () => {
    // toast.success("Tenant Created");
    // onRequestClose();
    const validationErrors = validateCurrentStep();

    if (validationErrors) {
      setErrors(validationErrors);
    } else {
      createTenant(setSkipLoader);
    }
  };

  const createTenant = async (loaderFunc) => {
    loaderFunc(true);
    const submitData = new FormData();
    let sname = formData.storeName;
    submitData.append("email", formData.email);
    submitData.append("userName", formData.userName);
    submitData.append("brandName", sname);
    submitData.append("currencyId", formData.iCurrencyId);
    submitData.append("storeTypeId", formData.storeTypeId);
    submitData.append("planId", formData.selectedPlan);
    submitData.append("billingcycleId", formData.subscriptionType);

    const keys = formData.storeConfigKeys.map((feild) => {
      return {
        key: feild.sKey,
        value: formData[feild.sKey],
      };
    });
    submitData.append("storeKeys", JSON.stringify(keys));
    submitData.append("sBrandLogo", formData.brandLogo);

    try {
      const response = await apiHandler({
        url: `${endpoint.CREATE_TENANT}`,
        method: "POST",
        data: submitData,
        authToken: authData,
        dispatch,
      });

      if (response.data.success) {
        setCurrentPage(1);
        setSearchTerm("");
        setCurrentStep(1);
        setFormData({
          userName: "",
          email: "",
          phone: "",
          storeName: "",
          store_url: "",
          storeType: "",
          selectedPlan: "",
          api_version: "",
          access_token: "",
          brandLogo: null,
          iCurrencyId: "",
          storeConfigKeys: [],
          selectedPlan: "",
        });
        onRequestClose();
        fetchTenants(tenantsPerPage, 1, "", excludeInactive);
        toast.success(response.data.message);
      } else {
        setCurrentPage(1);
        setSearchTerm("");
        toast.error(response.data.message);
        setCurrentStep(1);
        setFormData((prev) => {
          return {
            ...prev,
            userName: "",
            email: "",
            phone: "",
            storeName: "",
            store_url: "",
            storeType: "",
            selectedPlan: "",
            api_version: "",
            access_token: "",
            brandLogo: null,
            iCurrencyId: "",
            storeConfigKeys: [],
            selectedPlan: "",
          };
        });

        onRequestClose();
        fetchTenants(tenantsPerPage, 1, "", excludeInactive);
      }
    } catch (error) {
    
    } finally {
      loaderFunc(false);
    }
  };

  return {
    currentStep,
    plans,
    formData,
    errors,
    handleChange,
    handlePlanSelection,
    handleSubscriptionTypeSelection,
    getPlanPrice,
    nextStep,
    prevStep,
    handleFormSubmit,
    handleFileChange,
    setFormData,
    selectOptions,
    getFieldDescription,
    isStandard,
    setIsStandard,
    setCurrentStep,
    handleFormSkip,
    submitLoader,
    skipLoader,
    customLoader,
    totalSteps,
  };
};

export default useCreateTenantModal;
